import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { PageContainer } from '@/components/layout/PageContainer';

import { ClientsHeader } from '@/components/clients/ClientsHeader';
import { ClientsList } from '@/components/clients/client_list/ClientsList';
import { ClientFormPopup } from '@/components/clients/new_client/ClientFormPopup';

// Import TanStack Query hooks
import {
  useFetchClientsList,
  useFetchClientDetails,
  useFetchAllClientHealthMetrics,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
} from '@/hooks/useClients';
import type { ClientFullData, ClientStage, ClientHealthMetrics } from '@/types/client';
import { getWarmthLevel } from '@/types/client';
import { StageFilterBar, type StageFilter } from '@/components/clients/client_list/StageFilterBar';
import { ALL_STAGES } from '@/components/clients/shared/stageConstants';

// Define a type for the client data we will display in the list (subset of ClientListData)
// This ClientDisplayData is what ClientCard expects.
// We will map ClientListData to this.
export type ClientDisplayData = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  activeBookings: number;
  previousBookings: number;
  domain: string | null;
  physical_address: string | null;
  totalRevenue: number;
  lastBooking: string | null;
  stage: ClientStage;
  healthMetrics: ClientHealthMetrics | null;
};

// This is the type ClientForm expects. It should align with ClientFullData from clientService.
// For simplicity, we'll use ClientFullData directly for the form.
export type ClientFullConfigData = ClientFullData;


const ClientsPageContent: React.FC = () => {
  const { data: clientsList = [], isLoading: isLoadingClients, error: fetchError } = useFetchClientsList();
  const { data: allHealthMetrics = [] } = useFetchAllClientHealthMetrics();
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const deleteClientMutation = useDeleteClient();

  const [clientFormData, setClientFormData] = useState<Partial<ClientFullConfigData> | null>(null);
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [isFormPopupOpen, setIsFormPopupOpen] = useState(false);
  
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const { data: editingClientData, isLoading: isLoadingDetails, error: detailError } = useFetchClientDetails(editingClientId);
  
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 200);
  type SortField = 'name';
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
  const [stageFilter, setStageFilter] = useState<StageFilter>('all');

  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Build a lookup map for health metrics by client_id
  const healthMetricsMap = useMemo(() => {
    const map = new Map<string, ClientHealthMetrics>();
    for (const m of allHealthMetrics) {
      map.set(m.client_id, m);
    }
    return map;
  }, [allHealthMetrics]);

  const handleAddNewClientClick = useCallback(() => {
    setFormMode('add');
    // The form will now fetch defaults. We just need to provide an empty object.
    setClientFormData({ name: '' });
    createClientMutation.reset();
    setIsFormPopupOpen(true);
  }, [createClientMutation]);

  const displayClients: ClientDisplayData[] = useMemo(() => {
    return clientsList.map(client => {
      const id = client.id ?? '';
      const hm = healthMetricsMap.get(id) ?? null;
      return {
        id,
        name: client.name ?? 'Unnamed Client',
        domain: client.domain ?? null,
        physical_address: client.physical_address ?? null,
        email: null,
        phone: null,
        activeBookings: 0,
        previousBookings: 0,
        totalRevenue: hm?.total_revenue ?? 0,
        lastBooking: hm?.last_booking_date ?? null,
        stage: ((client as unknown as Record<string, unknown>).stage as ClientStage) || 'active',
        healthMetrics: hm,
      };
    });
  }, [clientsList, healthMetricsMap]);

  // Stage counts for the filter bar
  const stageCounts = useMemo(() => {
    const counts: Record<ClientStage, number> = { lead: 0, prospect: 0, active: 0, dormant: 0, inactive: 0 };
    for (const c of displayClients) {
      if (counts[c.stage] !== undefined) counts[c.stage]++;
    }
    return counts;
  }, [displayClients]);

  const filteredAndSortedClients = React.useMemo(() => {
    let filtered = displayClients.filter(client =>
      client.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      (client.domain && client.domain.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
    );

    // Apply stage filter
    if (stageFilter !== 'all') {
      filtered = filtered.filter(client => client.stage === stageFilter);
    }

    if (editingClientId) {
        const editingIndex = filtered.findIndex(c => c.id === editingClientId);
        if (editingIndex > 0) {
            const clientToEdit = filtered[editingIndex];
            filtered = [clientToEdit, ...filtered.slice(0, editingIndex), ...filtered.slice(editingIndex + 1)];
        } else if (editingIndex === -1) {
            const originalClient = displayClients.find(c => c.id === editingClientId);
            if (originalClient) {
                filtered = [originalClient, ...filtered];
            }
        }
    }
    
    const sortableClients = editingClientId ? filtered.slice(1) : filtered;
    sortableClients.sort((a, b) => {
      const valA = a.name?.toLowerCase() || '';
      const valB = b.name?.toLowerCase() || '';
      const comparison = valA < valB ? -1 : valA > valB ? 1 : 0;
      return sortDirection === 'asc' ? comparison : comparison * -1;
    });

    return editingClientId ? [filtered[0], ...sortableClients] : sortableClients;

  }, [displayClients, debouncedSearchTerm, sortDirection, editingClientId, stageFilter]);

  const handleSaveClient = useCallback(async (formData: Partial<ClientFullConfigData>) => {
    if (editingClientId && !formMode) { // Inline Edit Save
      await updateClientMutation.mutateAsync({ clientId: editingClientId, clientData: formData });
      setEditingClientId(null); 
    } else if (formMode === 'add') { // Sheet Add
      await createClientMutation.mutateAsync(formData);
      setIsFormPopupOpen(false);
      setFormMode(null);
    }
  }, [editingClientId, formMode, updateClientMutation, createClientMutation]);

  const handleCancelEdit = useCallback(() => { 
    setEditingClientId(null);
  }, []);

  const handleEditStart = useCallback((clientId: string) => {
    if (editingClientId === clientId) {
      handleCancelEdit();
      return;
    }
    setFormMode(null); 
    setIsFormPopupOpen(false); 
    setEditingClientId(clientId); 
    updateClientMutation.reset(); 
  }, [editingClientId, handleCancelEdit, updateClientMutation]);
  
  useEffect(() => {
    if (editingClientId && gridContainerRef.current) {
      const timer = setTimeout(() => {
        gridContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [editingClientId]);

  const handleRemoveClient = useCallback(async (clientId: string) => {
    try {
        await deleteClientMutation.mutateAsync(clientId);
    } catch (e) {
        // Error already logged by mutation's onError
    }
  }, [deleteClientMutation]);

  const handleCancelForm = useCallback(() => {
    setIsFormPopupOpen(false);
    setClientFormData(null);
    setFormMode(null);
  }, []);

  const handleSortDirectionChange = useCallback(() => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  }, []);
  
  return (
    <PageContainer title="Clients" subtitle="Manage your clients" isMobileNavHidden={isFormPopupOpen}>
      <div className="space-y-4">
        <ClientsHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortBy={sortBy}
          onSortChange={(value: SortField) => setSortBy(value)}
          sortDirection={sortDirection}
          onSortDirectionChange={handleSortDirectionChange}
          onAddNewClick={handleAddNewClientClick}
        />

        <StageFilterBar
          activeFilter={stageFilter}
          stageCounts={stageCounts}
          totalCount={displayClients.length}
          onFilterChange={setStageFilter}
        />

        <ClientsList
          clients={filteredAndSortedClients}
          isLoading={isLoadingClients}
          error={fetchError}
          editingClientId={editingClientId}
          editingClientData={editingClientData ?? null}
          isLoadingDetails={isLoadingDetails}
          detailError={detailError}
          updateMutation={updateClientMutation}
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          multiSelectEnabled={multiSelectEnabled}
          onSaveEdit={handleSaveClient}
          onCancelEdit={handleCancelEdit}
          onEditStart={handleEditStart}
          onRemove={handleRemoveClient}
          gridContainerRef={gridContainerRef}
        />

        {formMode === 'add' && clientFormData && (
          <ClientFormPopup
            isOpen={isFormPopupOpen}
            onOpenChange={(isOpen) => isOpen ? setIsFormPopupOpen(true) : handleCancelForm()}
            clientData={clientFormData}
            onSave={handleSaveClient}
            onCancel={handleCancelForm}
            isLoading={createClientMutation.isPending}
            error={createClientMutation.error?.message || null}
            allClients={displayClients}
          />
        )}
      </div>
    </PageContainer>
  );
};

const ClientsPage: React.FC = () => <ClientsPageContent />;

export default ClientsPage;
